import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { SessionContext } from '../../context/SessionContext';
import { getTelemetryEventsBySession } from '../workspace/service/telemetryService';

// Color palettes
const INTENT_COLORS = {
  'Learning': '#4CAF50',
  'Completion': '#2196F3', 
  'Debugging': '#FF9800',
  'Testing': '#9C27B0',
  'Other': '#607D8B'
};

const QUESTION_TYPE_COLORS = {
  'Task Clarification': '#3F51B5',
  'Syntax Error': '#F44336',
  'Implementation Challenge': '#FF9800',
  'Conceptual Question': '#9C27B0',
  'Runtime Error': '#E91E63',
  'Logic Error': '#00BCD4',
  'Other': '#607D8B'
};

const ERROR_COLORS = {
  'SyntaxError': '#F44336',
  'TypeError': '#E91E63',
  'NameError': '#9C27B0',
  'AttributeError': '#673AB7',
  'ValueError': '#3F51B5',
  'IndexError': '#2196F3',
  'KeyError': '#00BCD4',
  'Other': '#607D8B'
};

const OUTCOME_COLORS = ['#4CAF50', '#FFC107', '#FF9800', '#F44336'];
const HELP_COLORS = ['#2196F3', '#9E9E9E'];
const TIMING_COLORS = ['#4CAF50', '#FF9800', '#9E9E9E'];

const SessionAnalytics = ({ students, onAnalyticsReady }) => {
  const { session } = useContext(SessionContext);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const computeAnalytics = async () => {
      if (!session?.id || !students?.length) return;
      
      setLoading(true);
      
      try {
        // Sourced from telemetry_events, not the interaction/message/submission/
        // rrweb_events tables this used to read - those stopped being written on
        // 2026-08-21/22 (interaction dead since 2025-12-23), so this whole dashboard was
        // silently computing over empty data for any current session regardless of how
        // much a student actually did (e.g. a student who genuinely used the AI chat still
        // showed up as "No AI Interaction"). One session-wide fetch instead of N per-student
        // ones - getTelemetryEventsBySession already returns events grouped by user.
        const eventsByUser = await getTelemetryEventsBySession(session.id);

        const studentDataPromises = students.map(async (student) => {
          const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || [];

          const terminalErrors = events
            .filter(e => e.type === 'TERMINAL_ERROR')
            .map(e => ({
              timestamp: new Date(e.timestamp),
              text: `${e.payload?.type || 'Error'}: ${e.payload?.message || ''}`,
            }));

          // A chat message a student sent to the AI - matches the old `messages` shape
          // (just enough for the `.length > 0` / timing checks below, which never read
          // message content).
          const messages = events
            .filter(e => e.type === 'CHAT_QUERY')
            .map(e => ({ sender_id: student.id, created_time: new Date(e.timestamp) }));

          // "Code" here means a graded attempt (something with a pass rate), matching how
          // this file uses it below (`c.passrate === 100`, first-attempt ordering) - a
          // TEST_CASE_RESULT event is a closer match for that than a raw code snapshot.
          // Sessions with no formal test suite (only run/print, no asserted tests) will
          // correctly show 0 here rather than a fabricated pass rate.
          const codes = events
            .filter(e => e.type === 'TEST_CASE_RESULT')
            .map(e => ({ created_time: new Date(e.timestamp), passrate: e.payload?.passrate ?? 0 }));

          return {
            student,
            interactions: [], // no current-pipeline equivalent to the old LLM-intent
                               // classification (student_intent/question_type) - see the
                               // artifact's "Open decisions" for why
            messages,
            testRuns: codes,
            codes,
            terminalErrors,
          };
        });

        const studentData = await Promise.all(studentDataPromises);
        const allInteractions = [];
        const allMessages = studentData.flatMap(s => s.messages);

        // === COMPUTE ANALYTICS ===

        // 1. Intent Distribution
        const intentCounts = {};
        allInteractions.forEach(i => {
          const intent = i.student_intent || 'Other';
          intentCounts[intent] = (intentCounts[intent] || 0) + 1;
        });
        const intentData = Object.entries(intentCounts).map(([name, value]) => ({
          name,
          value,
          color: INTENT_COLORS[name] || INTENT_COLORS['Other']
        }));

        // 2. Question Type Distribution
        const questionTypeCounts = {};
        allInteractions.forEach(i => {
          const qType = i.question_type || 'Other';
          questionTypeCounts[qType] = (questionTypeCounts[qType] || 0) + 1;
        });
        const questionTypeData = Object.entries(questionTypeCounts).map(([name, value]) => ({
          name,
          value,
          color: QUESTION_TYPE_COLORS[name] || QUESTION_TYPE_COLORS['Other']
        }));

        // 3. Help-Seeking: Students who asked for help vs didn't
        const studentsWithHelp = studentData.filter(s => s.messages.length > 0).length;
        const studentsWithoutHelp = students.length - studentsWithHelp;
        const helpSeekingData = [
          { name: 'Asked for Help', value: studentsWithHelp, color: HELP_COLORS[0] },
          { name: 'No AI Interaction', value: studentsWithoutHelp, color: HELP_COLORS[1] }
        ];

        // 4. Help Timing: Before first error vs after errors
        let helpBeforeError = 0;
        let helpAfterError = 0;
        let noErrorsButHelped = 0;
        
        studentData.forEach(s => {
          if (s.messages.length === 0) return;
          
          const firstMessageTime = Math.min(...s.messages.map(m => new Date(m.created_time).getTime()));
          const firstErrorTime = s.terminalErrors.length > 0 
            ? Math.min(...s.terminalErrors.map(e => e.timestamp.getTime()))
            : null;
          
          if (firstErrorTime === null) {
            noErrorsButHelped++;
          } else if (firstMessageTime < firstErrorTime) {
            helpBeforeError++;
          } else {
            helpAfterError++;
          }
        });
        
        const helpTimingData = [
          { name: 'Before First Error', value: helpBeforeError, color: TIMING_COLORS[0] },
          { name: 'After Errors', value: helpAfterError, color: TIMING_COLORS[1] },
          { name: 'No Errors (but asked)', value: noErrorsButHelped, color: TIMING_COLORS[2] }
        ].filter(d => d.value > 0);

        // 5. Self-Resolution Rate
        let selfResolved = 0;
        let neededHelp = 0;
        let noErrors = 0;
        
        studentData.forEach(s => {
          const hadErrors = s.terminalErrors.length > 0;
          const askedForHelp = s.messages.length > 0;
          const eventuallyPassed = s.codes.some(c => c.passrate === 100);
          
          if (!hadErrors) {
            noErrors++;
          } else if (eventuallyPassed && !askedForHelp) {
            selfResolved++;
          } else if (hadErrors) {
            neededHelp++;
          }
        });
        
        const selfResolutionData = [
          { name: 'Self-Resolved', value: selfResolved, color: '#4CAF50' },
          { name: 'Needed AI Help', value: neededHelp, color: '#2196F3' },
          { name: 'No Errors', value: noErrors, color: '#9E9E9E' }
        ].filter(d => d.value > 0);

        // 6. Error Type Distribution
        const errorTypeCounts = {};
        studentData.forEach(s => {
          s.terminalErrors.forEach(err => {
            const text = err.text;
            let errorType = 'Other';
            
            if (text.includes('SyntaxError')) errorType = 'SyntaxError';
            else if (text.includes('TypeError')) errorType = 'TypeError';
            else if (text.includes('NameError')) errorType = 'NameError';
            else if (text.includes('AttributeError')) errorType = 'AttributeError';
            else if (text.includes('ValueError')) errorType = 'ValueError';
            else if (text.includes('IndexError')) errorType = 'IndexError';
            else if (text.includes('KeyError')) errorType = 'KeyError';
            
            errorTypeCounts[errorType] = (errorTypeCounts[errorType] || 0) + 1;
          });
        });
        
        const errorTypeData = Object.entries(errorTypeCounts)
          .map(([name, value]) => ({
            name,
            value,
            color: ERROR_COLORS[name] || ERROR_COLORS['Other']
          }))
          .sort((a, b) => b.value - a.value);

        // 7. Errors Before Success Distribution
        const errorsBeforeSuccessCounts = { '0': 0, '1': 0, '2-3': 0, '4+': 0 };
        
        studentData.forEach(s => {
          const passedCodes = s.codes.filter(c => c.passrate === 100);
          if (passedCodes.length === 0) return;
          
          const firstPassTime = Math.min(...passedCodes.map(c => new Date(c.created_time).getTime()));
          const errorsBeforePass = s.terminalErrors.filter(e => e.timestamp.getTime() < firstPassTime).length;
          
          if (errorsBeforePass === 0) errorsBeforeSuccessCounts['0']++;
          else if (errorsBeforePass === 1) errorsBeforeSuccessCounts['1']++;
          else if (errorsBeforePass <= 3) errorsBeforeSuccessCounts['2-3']++;
          else errorsBeforeSuccessCounts['4+']++;
        });
        
        const errorsBeforeSuccessData = [
          { name: '0 errors', value: errorsBeforeSuccessCounts['0'], color: '#4CAF50' },
          { name: '1 error', value: errorsBeforeSuccessCounts['1'], color: '#8BC34A' },
          { name: '2-3 errors', value: errorsBeforeSuccessCounts['2-3'], color: '#FFC107' },
          { name: '4+ errors', value: errorsBeforeSuccessCounts['4+'], color: '#FF5722' }
        ].filter(d => d.value > 0);

        // 8. First-Try Success Rate
        let firstTrySuccess = 0;
        let notFirstTry = 0;
        
        studentData.forEach(s => {
          if (s.codes.length === 0) return;
          
          const sortedCodes = [...s.codes].sort((a, b) => 
            new Date(a.created_time) - new Date(b.created_time)
          );
          
          if (sortedCodes[0]?.passrate === 100) {
            firstTrySuccess++;
          } else {
            notFirstTry++;
          }
        });
        
        const firstTryData = [
          { name: 'First Try Success', value: firstTrySuccess, color: '#4CAF50' },
          { name: 'Needed Iterations', value: notFirstTry, color: '#FF9800' }
        ].filter(d => d.value > 0);

        // 9. Final Pass Rate
        let passed = 0;
        let notPassed = 0;
        
        studentData.forEach(s => {
          const hasPassing = s.codes.some(c => c.passrate === 100);
          if (hasPassing) passed++;
          else notPassed++;
        });
        
        const passRateData = [
          { name: 'Passed', value: passed, color: '#4CAF50' },
          { name: 'Did Not Pass', value: notPassed, color: '#F44336' }
        ].filter(d => d.value > 0);

        // 10. Completion Stages
        let neverStarted = 0;
        let attempted = 0;
        let partial = 0;
        let complete = 0;
        
        studentData.forEach(s => {
          if (s.codes.length === 0) {
            neverStarted++;
          } else {
            const maxPassrate = Math.max(...s.codes.map(c => c.passrate || 0));
            if (maxPassrate === 100) complete++;
            else if (maxPassrate > 0) partial++;
            else attempted++;
          }
        });
        
        const completionStagesData = [
          { name: 'Complete (100%)', value: complete, color: OUTCOME_COLORS[0] },
          { name: 'Partial (1-99%)', value: partial, color: OUTCOME_COLORS[1] },
          { name: 'Attempted (0%)', value: attempted, color: OUTCOME_COLORS[2] },
          { name: 'Never Started', value: neverStarted, color: OUTCOME_COLORS[3] }
        ].filter(d => d.value > 0);

        // Summary stats
        const summaryStats = {
          totalStudents: students.length,
          totalInteractions: allInteractions.length,
          totalMessages: allMessages.filter(m => students.some(s => s.id === m.sender_id)).length,
          avgInteractionsPerStudent: (allInteractions.length / students.length).toFixed(1),
          helpSeekingRate: ((studentsWithHelp / students.length) * 100).toFixed(0),
          passRate: ((passed / students.length) * 100).toFixed(0),
          firstTryRate: students.length > 0 ? ((firstTrySuccess / (firstTrySuccess + notFirstTry)) * 100).toFixed(0) : 0
        };

        setAnalytics({
          intentData,
          questionTypeData,
          helpSeekingData,
          helpTimingData,
          selfResolutionData,
          errorTypeData,
          errorsBeforeSuccessCounts,
          errorsBeforeSuccessData,
          firstTryData,
          passRateData,
          completionStagesData,
          summaryStats,
          // Raw counts for export
          rawCounts: {
            intentCounts,
            questionTypeCounts,
            errorTypeCounts,
            studentsWithHelp,
            studentsWithoutHelp,
            helpBeforeError,
            helpAfterError,
            noErrorsButHelped,
            selfResolved,
            neededHelp,
            noErrors,
            firstTrySuccess,
            notFirstTry,
            passed,
            notPassed,
            complete,
            partial,
            attempted,
            neverStarted
          }
        });

      } catch (error) {
        console.error('Error computing analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    computeAnalytics();
  }, [session?.id, students]);

  useEffect(() => {
    if (analytics && onAnalyticsReady) {
      const exportData = {
        session_id: session?.id,
        session_subject: session?.subject,
        export_timestamp: new Date().toISOString(),
        
        summary: {
          total_students: analytics.summaryStats.totalStudents,
          total_ai_interactions: analytics.summaryStats.totalInteractions,
          total_student_messages: analytics.summaryStats.totalMessages,
          avg_interactions_per_student: parseFloat(analytics.summaryStats.avgInteractionsPerStudent),
          help_seeking_rate_percent: parseFloat(analytics.summaryStats.helpSeekingRate),
          pass_rate_percent: parseFloat(analytics.summaryStats.passRate),
          first_try_success_rate_percent: parseFloat(analytics.summaryStats.firstTryRate)
        },

        student_intent_distribution: analytics.intentData.map(d => ({
          intent: d.name,
          count: d.value,
          percentage: ((d.value / analytics.summaryStats.totalInteractions) * 100).toFixed(1)
        })),

        question_type_distribution: analytics.questionTypeData.map(d => ({
          question_type: d.name,
          count: d.value,
          percentage: ((d.value / analytics.summaryStats.totalInteractions) * 100).toFixed(1)
        })),

        help_seeking_patterns: {
          students_who_asked_for_help: analytics.rawCounts.studentsWithHelp,
          students_with_no_ai_interaction: analytics.rawCounts.studentsWithoutHelp,
          help_timing: {
            asked_before_first_error: analytics.rawCounts.helpBeforeError,
            asked_after_errors: analytics.rawCounts.helpAfterError,
            no_errors_but_asked: analytics.rawCounts.noErrorsButHelped
          },
          self_resolution: {
            self_resolved_without_ai: analytics.rawCounts.selfResolved,
            needed_ai_help: analytics.rawCounts.neededHelp,
            no_errors_encountered: analytics.rawCounts.noErrors
          }
        },

        error_patterns: {
          error_type_distribution: analytics.errorTypeData.map(d => ({
            error_type: d.name,
            count: d.value
          })),
          errors_before_success: {
            zero_errors: analytics.errorsBeforeSuccessCounts['0'],
            one_error: analytics.errorsBeforeSuccessCounts['1'],
            two_to_three_errors: analytics.errorsBeforeSuccessCounts['2-3'],
            four_plus_errors: analytics.errorsBeforeSuccessCounts['4+']
          },
          first_try_success: {
            succeeded_first_try: analytics.rawCounts.firstTrySuccess,
            needed_iterations: analytics.rawCounts.notFirstTry
          }
        },

        outcome_metrics: {
          final_pass_rate: {
            passed: analytics.rawCounts.passed,
            did_not_pass: analytics.rawCounts.notPassed
          },
          completion_stages: {
            complete_100_percent: analytics.rawCounts.complete,
            partial_1_to_99_percent: analytics.rawCounts.partial,
            attempted_0_percent: analytics.rawCounts.attempted,
            never_started: analytics.rawCounts.neverStarted
          }
        }
      };
      
      onAnalyticsReady(exportData);
    }
  }, [analytics, session, onAnalyticsReady]);

  const renderPieChart = (data, title, subtitle) => (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        height: '100%',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1a1a1a', mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      
      {data && data.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              innerRadius={40}
              outerRadius={60}
              // KNOWN ISSUE, not fixed: with a single 100%-share category (common for a
              // small/lightly-used session - e.g. one student who asked for help out of
              // one total), this renders as a thin sliver with no label instead of a
              // full ring - recharts' padding-angle math degenerates for a single sector
              // (tried paddingAngle=0 for this case instead: that renders nothing at all,
              // strictly worse, reverted). The underlying numbers are correct - confirmed
              // via "Copy Analytics JSON" - this is a display-only recharts quirk.
              paddingAngle={3}
              dataKey="value"
              label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : null}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [value, name]}
              contentStyle={{ 
                borderRadius: 8, 
                border: 'none', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                padding: '8px 12px',
                fontSize: '0.85rem'
              }}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
              formatter={(value) => (
                <span style={{ color: '#333', fontSize: '11px', marginLeft: '2px' }}>
                  {value}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <Typography color="text.secondary" fontSize="0.85rem">No data available</Typography>
        </Box>
      )}
    </Paper>
  );

  const renderBarChart = (data, title, subtitle) => (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        height: '100%',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1a1a1a', mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      
      {data && data.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={true} vertical={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#666' }} axisLine={{ stroke: '#e0e0e0' }} />
            <YAxis 
              type="category" 
              dataKey="name" 
              width={120}
              tick={{ fontSize: 11, fill: '#333' }}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <Tooltip 
              contentStyle={{ 
                borderRadius: 8, 
                border: 'none', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                padding: '8px 12px',
                fontSize: '0.85rem'
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <Typography color="text.secondary" fontSize="0.85rem">No data available</Typography>
        </Box>
      )}
    </Paper>
  );

  const SectionHeader = ({ title }) => (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Typography 
        variant="subtitle1" 
        fontWeight={600} 
        sx={{ color: '#444' }}
      >
        {title}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 10,
          minHeight: 300
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography sx={{ mt: 2, fontSize: '0.95rem' }} color="text.secondary">
          Computing analytics for {students.length} students...
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: '0.85rem' }} color="text.disabled">
          This may take a moment
        </Typography>
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography color="text.secondary" fontSize="0.95rem">
          No analytics data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Intent & Question Type */}
      <SectionHeader title="Student Intent & Question Types" />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {renderPieChart(
            analytics.intentData,
            'Student Intent Distribution',
            'Why students reached out to the AI'
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderPieChart(
            analytics.questionTypeData,
            'Question Type Distribution',
            'Types of questions students asked'
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Help-Seeking Patterns */}
      <SectionHeader title="Help-Seeking Patterns" />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          {renderPieChart(
            analytics.helpSeekingData,
            'AI Interaction Rate',
            '% of students who asked for help'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderPieChart(
            analytics.helpTimingData,
            'Help Timing',
            'When students first asked for help'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderPieChart(
            analytics.selfResolutionData,
            'Self-Resolution',
            'Students who solved errors without AI'
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Error Patterns */}
      <SectionHeader title="Error Patterns" />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          {renderBarChart(
            analytics.errorTypeData,
            'Error Type Distribution',
            'Most common Python errors'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderPieChart(
            analytics.errorsBeforeSuccessData,
            'Errors Before Success',
            'How many errors before passing'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderPieChart(
            analytics.firstTryData,
            'First-Try Success Rate',
            'Students who passed on first attempt'
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Outcome Metrics */}
      <SectionHeader title="Outcome Metrics" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {renderPieChart(
            analytics.passRateData,
            'Final Pass Rate',
            'Students who eventually passed all tests'
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderPieChart(
            analytics.completionStagesData,
            'Completion Stages',
            'Where students ended up'
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SessionAnalytics;